import React, { useContext, useEffect, useState } from "react";
import ServiceForm from "../ServiceForm/ServiceForm";
import useServiceFetch from "../../../hooks/useServiceFetch/useServiceFetch";
import ServiceService from "../../../services/ServiceService/ServiceService";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  doCleanServiceFormValues,
  doCleanServiceImage,
  doCleanDefaultValues,
  doHandleHiddenFieldVisibility,
  formatServiceCategories,
  formatServiceDemographics,
  generateInitialShowHiddenField,
} from "../../../utils/functions/serviceFunctions";
import AppLoading from "../../../AppLoading";
import TaxonomiesService from "../../../services/TaxonomiesService/TaxonomiesService";
import UserContext from "../../../context/UserContext/UserContext";
import { checkIsInternalTeam } from "../../../utils/functions/functions";
import BASE_API_URL from "../../../settings/baseApiUrl";

const EditService = (props) => {
  const { serviceId: serviceIdParam } = useParams();
  const serviceId = props.serviceId ?? serviceIdParam;

  const { service, isLoading: fetchIsLoading } = useServiceFetch(serviceId);
  const navigate = useNavigate();

  const user = useContext(UserContext)[0];
  const isInternalTeam = checkIsInternalTeam(user.roles);

  const [defaultValues, setDefaultValues] = useState({});

  const [submitIsLoading, setSubmitIsLoading] = useState(false);
  const [serviceImageIsLoading, setServiceImageIsLoading] = useState(false);

  const [taxonomiesIsLoading, setTaxonomiesIsLoading] = useState(true);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [serviceDemographics, setServiceDemographics] = useState([]);

  const [showHiddenField, setShowHiddenField] = useState([]);

  useEffect(() => {
    async function fetchTaxonomies() {
      const taxonomies = await TaxonomiesService.retrieveTaxonomies();

      setTaxonomiesIsLoading(false);

      if (taxonomies) {
        const categories =
          taxonomies.categories ?? taxonomies.taxonomies?.categories ?? [];
        const demographics =
          taxonomies.demographics ?? taxonomies.taxonomies?.demographics ?? [];

        const formattedServiceCategories = formatServiceCategories({
          serviceCategories: categories,
        });

        const formattedServiceDemographics = formatServiceDemographics({
          serviceDemographics: demographics,
        });

        setServiceCategories(formattedServiceCategories);
        setServiceDemographics(formattedServiceDemographics);
        setShowHiddenField(generateInitialShowHiddenField(formattedServiceCategories));
      } else {
        toast.error("An error occured, please try again.");
        navigate("/service");
      }
    }

    fetchTaxonomies();
  }, [setServiceCategories, setServiceDemographics, setShowHiddenField]);

  useEffect(() => {
    if (fetchIsLoading) return;

    const cleanDefaultValues = doCleanDefaultValues(
      service,
      serviceCategories,
      serviceDemographics,
    );

    if (serviceId && cleanDefaultValues.image) {
      cleanDefaultValues.image = {
        ...cleanDefaultValues.image,
        medium: `${BASE_API_URL}/images/${serviceId}/medium`,
        original: `${BASE_API_URL}/images/${serviceId}/original`,
      };
    }

    if (serviceCategories.length > 0 && serviceDemographics.length > 0) {
      doHandleHiddenFieldVisibility(
        cleanDefaultValues,
        showHiddenField,
        setShowHiddenField,
        serviceCategories,
      );

      setDefaultValues(cleanDefaultValues);
    }
  }, [
    service,
    serviceCategories,
    serviceDemographics,
    showHiddenField,
    fetchIsLoading,
    setDefaultValues,
    setShowHiddenField,
  ]);

  async function doEditService(formValues) {
    if (submitIsLoading) return;

    const cleanFormValues = doCleanServiceFormValues(
      formValues,
      serviceCategories,
      serviceDemographics,
    );

    cleanFormValues.updated_at = new Date();

    const serviceImage = doCleanServiceImage(cleanFormValues.image);

    delete cleanFormValues.image;

    setSubmitIsLoading(true);

    console.log(cleanFormValues);

    const updatedService = await ServiceService.updateService(serviceId, cleanFormValues);

    const returnedServiceImage = await ServiceService.createServiceImage(
      serviceId,
      serviceImage,
    );

    setSubmitIsLoading(false);

    if (updatedService && (returnedServiceImage || !cleanFormValues.image)) {
      toast.success(`Service updated.`);

      navigate("/service");
    } else if (updatedService && !returnedServiceImage) {
      toast.warning(
        `New service ${updatedService.name} created but service image failed to upload.`,
      );

      navigate("/service");
    } else {
      toast.error("Unable to update service.");
    }
  }

  if (
    fetchIsLoading ||
    Object.keys(defaultValues).length === 0 ||
    serviceImageIsLoading ||
    taxonomiesIsLoading
  ) {
    return <AppLoading />;
  }

  return (
    <>
      <ServiceForm
        initialStepId={isInternalTeam ? "organisation" : "details"}
        pageTitle={"Edit your service listing"}
        onFormCompletion={doEditService}
        defaultValues={defaultValues}
        showHiddenField={showHiddenField}
        setShowHiddenField={setShowHiddenField}
        submitLoading={submitIsLoading}
        serviceCategories={serviceCategories}
        serviceDemographics={serviceDemographics}
      />
    </>
  );
};

export default EditService;
